import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import type {
  AddBranchAuthorizationInput,
  CarrierBranchAuthorizationRow,
  CarrierVersionInput,
  CarrierVersionRow,
  CarrierView,
  CreateCarrierInput,
  LineRow,
} from "../../api/catalog-contracts";

// Catalog self-service (carrier + version + line-authorization). The governed,
// versioned catalog pattern (ADR 0004): identity + append-only versions. No business
// logic in the browser — the API is the boundary and re-enforces catalog:write @ all.
// The carrier LIST read (`useCarriers`) lives beside the scorecard in `use-catalog.ts`;
// these are the management reads/writes.

const CARRIERS = ["catalog", "carriers"];
const versionsKey = (code: string) => [...CARRIERS, code, "versions"];
const authorizationsKey = (code: string) => [...CARRIERS, code, "authorizations"];

/** GET /v1/catalog/lines — the tri-pillar taxonomy (the branch/line picker).
 *  catalog:read @ all. */
export function useCatalogLines() {
  const api = useApi();
  return useQuery({
    queryKey: ["catalog", "lines"],
    queryFn: () => api.get<Page<LineRow>>("/v1/catalog/lines"),
  });
}

/** POST /v1/catalog/carriers — create a carrier (identity + version 1). catalog:write
 *  @ all, audited, append-only. An already-existing code returns 409. On success,
 *  refetch the carrier list so the new carrier appears. */
export function useCreateCarrier() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCarrierInput) => api.post<CarrierView>("/v1/catalog/carriers", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CARRIERS });
    },
  });
}

/** GET /v1/catalog/carriers/:code/versions — the append-only version timeline (the
 *  contract's guaranteed history). catalog:read @ all. */
export function useCarrierVersions(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? versionsKey(code) : [...CARRIERS, "none", "versions"],
    enabled: code !== null,
    queryFn: () => api.get<Page<CarrierVersionRow>>(`/v1/catalog/carriers/${code}/versions`),
  });
}

/** POST /v1/catalog/carriers/:code/versions — append a new version (closes the open
 *  one, opens the successor — NEVER an in-place edit). catalog:write @ all, audited.
 *  On success, refetch the history + carrier list (the current version changed). */
export function useAddVersion(code: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CarrierVersionInput) =>
      api.post<CarrierView>(`/v1/catalog/carriers/${code}/versions`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: versionsKey(code) });
      void qc.invalidateQueries({ queryKey: [...CARRIERS, "list"] });
    },
  });
}

/** GET /v1/catalog/carriers/:code/authorizations — the carrier's branch (line)
 *  authorizations. catalog:read @ all. */
export function useAuthorizations(code: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: code ? authorizationsKey(code) : [...CARRIERS, "none", "authorizations"],
    enabled: code !== null,
    queryFn: () =>
      api.get<Page<CarrierBranchAuthorizationRow>>(`/v1/catalog/carriers/${code}/authorizations`),
  });
}

/** POST /v1/catalog/carriers/:code/authorizations — record a branch (line)
 *  authorization, effective-dated. catalog:write @ all, audited. An already-open
 *  authorization for the same line returns 409. On success, refetch the list. */
export function useAddAuthorization(code: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddBranchAuthorizationInput) =>
      api.post<CarrierBranchAuthorizationRow>(`/v1/catalog/carriers/${code}/authorizations`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authorizationsKey(code) });
    },
  });
}
